import type { LatLon } from "@radar-sim/schema";

/**
 * Earth radius in nautical miles. Mean radius (WGS-84-ish), good enough
 * for tactical-scale radar work — error vs Vincenty is <0.5% at the ranges
 * we care about (under 50 nm).
 */
export const EARTH_RADIUS_NM = 3440.065;

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/** Normalise a bearing to [0, 360). */
export function normaliseBearing(b: number): number {
  const x = b % 360;
  return x < 0 ? x + 360 : x;
}

/** Great-circle distance in nautical miles using the haversine formula. */
export function distanceNm(a: LatLon, b: LatLon): number {
  const dLat = (b.lat - a.lat) * DEG_TO_RAD;
  const dLon = (b.lon - a.lon) * DEG_TO_RAD;
  const lat1 = a.lat * DEG_TO_RAD;
  const lat2 = b.lat * DEG_TO_RAD;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing in degrees true from a to b. */
export function bearingDeg(a: LatLon, b: LatLon): number {
  const lat1 = a.lat * DEG_TO_RAD;
  const lat2 = b.lat * DEG_TO_RAD;
  const dLon = (b.lon - a.lon) * DEG_TO_RAD;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return normaliseBearing(Math.atan2(y, x) * RAD_TO_DEG);
}

/**
 * Project `start` along a course `cogDeg` for `distanceNm` and return the
 * resulting position. Forward direct geodesic via spherical model.
 */
export function projectPosition(
  start: LatLon,
  cogDeg: number,
  distNm: number,
): LatLon {
  const lat1 = start.lat * DEG_TO_RAD;
  const lon1 = start.lon * DEG_TO_RAD;
  const angDist = distNm / EARTH_RADIUS_NM;
  const cog = cogDeg * DEG_TO_RAD;

  const sinLat2 =
    Math.sin(lat1) * Math.cos(angDist) +
    Math.cos(lat1) * Math.sin(angDist) * Math.cos(cog);
  const lat2 = Math.asin(sinLat2);
  const y = Math.sin(cog) * Math.sin(angDist) * Math.cos(lat1);
  const x = Math.cos(angDist) - Math.sin(lat1) * sinLat2;
  const lon2 = lon1 + Math.atan2(y, x);

  // Wrap longitude to [-180, 180]
  const lonDeg = ((((lon2 * RAD_TO_DEG + 540) % 360) + 360) % 360) - 180;
  return { lat: lat2 * RAD_TO_DEG, lon: lonDeg };
}

/**
 * Closest point of approach. Returns CPA distance in nautical miles and
 * TCPA in minutes (negative if the closest point is in the past — i.e. the
 * targets are opening).
 *
 * Inputs use a small-area flat approximation: own and target positions are
 * converted to a local nm-scaled XY plane centred on `own`. For ranges
 * under ~30 nm this is accurate to well under 0.1 nm of CPA error.
 */
export function computeCPA(
  own: { position: LatLon; cog: number; sog: number },
  target: { position: LatLon; cog: number; sog: number },
): { rangeNm: number; bearingDeg: number; cpaNm: number; tcpaMin: number } {
  const range = distanceNm(own.position, target.position);
  const brg = bearingDeg(own.position, target.position);

  // Convert to local XY (nm) with own at origin. East = +x, North = +y.
  const tx = range * Math.sin(brg * DEG_TO_RAD);
  const ty = range * Math.cos(brg * DEG_TO_RAD);

  // Velocities in nm/hour, broken into east/north components.
  const ownVx = own.sog * Math.sin(own.cog * DEG_TO_RAD);
  const ownVy = own.sog * Math.cos(own.cog * DEG_TO_RAD);
  const tgtVx = target.sog * Math.sin(target.cog * DEG_TO_RAD);
  const tgtVy = target.sog * Math.cos(target.cog * DEG_TO_RAD);

  // Relative velocity of target as seen from own.
  const rvx = tgtVx - ownVx;
  const rvy = tgtVy - ownVy;
  const rvMag2 = rvx * rvx + rvy * rvy;

  if (rvMag2 < 1e-9) {
    // No relative motion; targets stay at current range forever.
    return {
      rangeNm: range,
      bearingDeg: brg,
      cpaNm: range,
      tcpaMin: Number.POSITIVE_INFINITY,
    };
  }

  // tcpa in hours; CPA is the position of the relative track at that t.
  const tcpaHours = -(tx * rvx + ty * rvy) / rvMag2;
  const cpaX = tx + rvx * tcpaHours;
  const cpaY = ty + rvy * tcpaHours;
  const cpa = Math.sqrt(cpaX * cpaX + cpaY * cpaY);

  return {
    rangeNm: range,
    bearingDeg: brg,
    cpaNm: cpa,
    tcpaMin: tcpaHours * 60,
  };
}
